import { useConsoleLayout } from "../../stores/console";
import { useSession } from "../../stores/session";
import { useStudioSettings } from "../../stores/studio";

/**
 * Whether this browser is the one driving a broadcast.
 *
 * ## What it decides
 *
 * Two things, and they are the same question asked twice:
 *
 * - whether the deck plays here. In a live room the deck is loaded in exactly
 *   one place, and everywhere else the stream already carries it. Getting this
 *   wrong is either silence or the same music twice, a second apart.
 * - whether the host's own profile plays the stream back at them. It must not:
 *   that is their own audio, delayed, over the top of the deck they are
 *   playing from.
 *
 * ## Why external counts
 *
 * It did not, and that was the bug. `open` means the browser studio is up, so
 * a host broadcasting from OBS had it false - and both answers above came out
 * backwards. The deck never loaded, so there was no music and no transport in
 * the dock to explain where it went.
 *
 * The queue is still the room's music in external mode; the difference is only
 * that the encoder captures it from the machine rather than the page mixing it.
 * So the deck has to run here just as much as it does for the studio - arguably
 * more, because nothing else is playing it.
 */
export function useHostingHere(username: string): boolean {
  const isHost = useSession((state) => state.user?.username === username);
  const studioOpen = useStudioSettings((state) => state.open);
  const mode = useConsoleLayout((state) => state.broadcastMode);

  return isHost && (studioOpen || mode === "external");
}
